import axios from 'axios';
import React, { FormEvent, useState } from 'react';
import { Link } from 'react-router-dom';
import LandingFooter from '../components/landing/LandingFooter';
import LandingNav from '../components/landing/LandingNav';
import '../styles/landing.css';

// Same-origin in production (SPA served by API); localhost API in CRA dev.
const API_BASE =
  process.env.REACT_APP_API_URL !== undefined
    ? process.env.REACT_APP_API_URL
    : process.env.NODE_ENV === 'production'
      ? ''
      : 'http://localhost:5000';
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const MIN_MESSAGE_LENGTH = 10;

type ContactFieldErrors = {
  name?: string;
  email?: string;
  message?: string;
};

const validateContactForm = (name: string, email: string, message: string): ContactFieldErrors => {
  const errors: ContactFieldErrors = {};
  const trimmedName = name.trim();
  const trimmedEmail = email.trim();
  const trimmedMessage = message.trim();

  if (!trimmedName) {
    errors.name = 'Name is required.';
  }

  if (!trimmedEmail) {
    errors.email = 'Email is required.';
  } else if (!EMAIL_PATTERN.test(trimmedEmail)) {
    errors.email = 'Enter a valid email address.';
  }

  if (!trimmedMessage) {
    errors.message = 'Message is required.';
  } else if (trimmedMessage.length < MIN_MESSAGE_LENGTH) {
    errors.message = `Message must be at least ${MIN_MESSAGE_LENGTH} characters.`;
  }

  return errors;
};

const submitContactForm = async (payload: { name: string; email: string; message: string }) => {
  await axios.post(`${API_BASE}/api/public/contact`, payload, { withCredentials: true });
};

const FEATURES = [
  {
    id: 'dashboard',
    title: 'See your cellar at a glance',
    body: 'A production dashboard that surfaces vineyards, active batches, vessel capacity, and bottling progress — so your team knows what needs attention today.',
    image: '/landing/dashboard.png',
    alt: 'Winery dashboard showing production KPIs, pipeline stages, and portfolio charts',
  },
  {
    id: 'vineyards',
    title: 'Manage vineyards and maintenance',
    body: 'Track plots, varieties, and field work in one place. Keep maintenance history with the vineyard so harvest decisions stay grounded in real records.',
    image: '/landing/vineyards.png',
    alt: 'Vineyards management screen listing vineyard plots and details',
  },
  {
    id: 'fermentation',
    title: 'Follow every fermentation',
    body: 'Log fermentation and post-fermentation work against each batch. Move wine through the process with a clear trail from crush to cellar.',
    image: '/landing/fermentation.png',
    alt: 'Fermentation section showing active fermentation processes and status',
  },
  {
    id: 'aging',
    title: 'Age with intention',
    body: 'Monitor aging stages, vessel assignments, and cellar maintenance so barrel and tank time stays organized across your team.',
    image: '/landing/aging.png',
    alt: 'Aging section showing wines maturing in barrels and tanks',
  },
  {
    id: 'inventory',
    title: 'Inventory you can trust',
    body: 'Track bottled stock, external sources, and movements. Know what you have — and where it came from — without spreadsheet archaeology.',
    image: '/landing/inventory.png',
    alt: 'Inventory screen showing bottle stock and transaction history',
  },
] as const;

const STEPS = [
  {
    step: '01',
    title: 'Track production end to end',
    body: 'Connect vineyards, batches, fermentation, aging, and bottling in a single operational timeline.',
  },
  {
    step: '02',
    title: 'Manage vessels and stock',
    body: 'Keep tanks, barrels, and finished goods visible so capacity and inventory never surprise you.',
  },
  {
    step: '03',
    title: 'Collaborate with roles',
    body: 'Invite your team with role-based access so cellar, production, and admin work stay appropriately scoped.',
  },
] as const;

/**
 * Public marketing landing for Wine Management System (B2B winery operations).
 */
const Landing: React.FC = () => {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [fieldErrors, setFieldErrors] = useState<ContactFieldErrors>({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [formStatus, setFormStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [statusMessage, setStatusMessage] = useState('');

  const handleContactSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFormStatus('idle');
    setStatusMessage('');

    const errors = validateContactForm(name, email, message);
    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      return;
    }

    setFieldErrors({});
    setIsSubmitting(true);

    try {
      await submitContactForm({
        name: name.trim(),
        email: email.trim(),
        message: message.trim(),
      });
      setFormStatus('success');
      setStatusMessage('Thanks — we received your message and will be in touch soon.');
      setName('');
      setEmail('');
      setMessage('');
    } catch {
      setFormStatus('error');
      setStatusMessage('We could not send your message. Please try again in a moment.');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div id="top" className="landing">
      <a href="#main" className="landing-skip">
        Skip to main content
      </a>

      <LandingNav />

      <main id="main">
        {/* Hero — brand first, one composition */}
        <section className="landing-hero" aria-labelledby="landing-hero-heading">
          <div className="landing-hero__atmosphere" aria-hidden="true" />
          <div className="landing-hero__content">
            <p className="landing-hero__brand">Winery</p>
            <h1 id="landing-hero-heading" className="landing-hero__title">
              Wine production,
              <span className="landing-hero__title-em"> under control</span>
            </h1>
            <p className="landing-hero__lede">
              The operations system for winery teams — vineyard to bottle, in one place.
            </p>
            <div className="landing-hero__ctas">
              <Link to="/login" className="landing-btn landing-btn--primary landing-btn--lg">
                Sign in
              </Link>
              <a href="#contact" className="landing-btn landing-btn--ghost landing-btn--lg">
                Request access
              </a>
            </div>
          </div>
        </section>

        {/* Product story */}
        <section id="product" className="landing-section landing-story" aria-labelledby="product-heading">
          <div className="landing-section__inner landing-story__grid">
            <div>
              <p className="landing-eyebrow">What we do</p>
              <h2 id="product-heading" className="landing-heading">
                One system for the full production path
              </h2>
              <p className="landing-prose">
                Wine Management System is built for winery operations — not consumer cellars or
                marketplaces. Track grapes from the vineyard through batches, fermentation, aging,
                bottling, and inventory, with vessels, blend tools, and team permissions alongside
                the work.
              </p>
            </div>
            <ol className="landing-pipeline" aria-label="Production pipeline">
              {['Vineyard', 'Batch', 'Ferment', 'Age', 'Bottle', 'Stock'].map((stage, i) => (
                <li key={stage} className="landing-pipeline__item">
                  <span className="landing-pipeline__index" aria-hidden="true">
                    {String(i + 1).padStart(2, '0')}
                  </span>
                  <span>{stage}</span>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* Feature showcase with real product screenshots */}
        <section id="features" className="landing-section landing-features" aria-labelledby="features-heading">
          <div className="landing-section__inner">
            <div className="landing-section__intro">
              <p className="landing-eyebrow">Inside the product</p>
              <h2 id="features-heading" className="landing-heading">
                Built for how wine is actually made
              </h2>
              <p className="landing-prose landing-prose--narrow">
                Real screens from the app — production dashboards, vineyard records, cellar stages,
                and inventory — designed for daily winery work.
              </p>
            </div>

            <div className="landing-features__list">
              {FEATURES.map((feature, index) => (
                <article
                  key={feature.id}
                  className={`landing-feature ${index % 2 === 1 ? 'landing-feature--flip' : ''}`}
                >
                  <div className="landing-feature__copy">
                    <h3 className="landing-feature__title">{feature.title}</h3>
                    <p className="landing-prose">{feature.body}</p>
                  </div>
                  <figure className="landing-feature__figure">
                    <div className="landing-device">
                      <img
                        src={feature.image}
                        alt={feature.alt}
                        width={1440}
                        height={1000}
                        loading={index === 0 ? 'eager' : 'lazy'}
                        decoding="async"
                      />
                    </div>
                  </figure>
                </article>
              ))}
            </div>

            <aside className="landing-callout" aria-labelledby="blend-heading">
              <div>
                <h3 id="blend-heading" className="landing-feature__title">
                  Wine Blend Calculator
                </h3>
                <p className="landing-prose">
                  Model alcohol, pH, acidity, and sugar when blending lots — a specialist tool
                  beside your production records.
                </p>
              </div>
              <div className="landing-device landing-device--compact">
                <img
                  src="/landing/blend-calculator.png"
                  alt="Wine blend calculator with chemistry inputs and blend results"
                  width={1440}
                  height={1000}
                  loading="lazy"
                  decoding="async"
                />
              </div>
            </aside>
          </div>
        </section>

        {/* How it works */}
        <section
          id="how-it-works"
          className="landing-section landing-steps"
          aria-labelledby="how-heading"
        >
          <div className="landing-section__inner">
            <div className="landing-section__intro">
              <p className="landing-eyebrow">How it works</p>
              <h2 id="how-heading" className="landing-heading">
                Simple for teams. Serious for production.
              </h2>
            </div>
            <ol className="landing-steps__grid">
              {STEPS.map((item) => (
                <li key={item.step} className="landing-steps__item">
                  <span className="landing-steps__num" aria-hidden="true">
                    {item.step}
                  </span>
                  <h3 className="landing-steps__title">{item.title}</h3>
                  <p className="landing-prose">{item.body}</p>
                </li>
              ))}
            </ol>
          </div>
        </section>

        {/* Trust / for whom */}
        <section className="landing-section landing-trust" aria-labelledby="trust-heading">
          <div className="landing-section__inner landing-trust__panel">
            <p className="landing-eyebrow">Who it is for</p>
            <h2 id="trust-heading" className="landing-heading">
              Built for winery teams
            </h2>
            <p className="landing-prose landing-prose--narrow">
              Access is invite-only. Accounts are provisioned by your administrator — this is
              operations software for licensed winery businesses, not a consumer tasting journal
              or wine shop.
            </p>
            <Link to="/login" className="landing-btn landing-btn--primary">
              Sign in to your workspace
            </Link>
          </div>
        </section>

        {/* Contact — intentionally empty / coming soon */}
        <section id="contact" className="landing-section landing-contact" aria-labelledby="contact-heading">
          <div className="landing-section__inner landing-contact__grid">
            <div>
              <p className="landing-eyebrow">Contact</p>
              <h2 id="contact-heading" className="landing-heading">
                Request access
              </h2>
              <p className="landing-prose">
                Tell us about your winery and we will follow up about access. If you already have
                an account, sign in to continue.
              </p>
            </div>

            <form
              className="landing-contact__form"
              aria-describedby="contact-helper"
              onSubmit={handleContactSubmit}
              noValidate
            >
              <p id="contact-helper" className="landing-contact__helper">
                We typically respond within one business day.
              </p>

              {formStatus !== 'idle' && (
                <p
                  className={`landing-contact__feedback landing-contact__feedback--${formStatus}`}
                  role="status"
                  aria-live="polite"
                >
                  {statusMessage}
                </p>
              )}

              <div className="landing-field">
                <label htmlFor="contact-name">Name</label>
                <input
                  id="contact-name"
                  name="name"
                  type="text"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  placeholder="Your name"
                  autoComplete="name"
                  required
                  aria-invalid={Boolean(fieldErrors.name)}
                  aria-describedby={fieldErrors.name ? 'contact-name-error' : undefined}
                />
                {fieldErrors.name && (
                  <span id="contact-name-error" className="landing-field__error" role="status">
                    {fieldErrors.name}
                  </span>
                )}
              </div>
              <div className="landing-field">
                <label htmlFor="contact-email">Email</label>
                <input
                  id="contact-email"
                  name="email"
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  placeholder="you@winery.com"
                  autoComplete="email"
                  required
                  aria-invalid={Boolean(fieldErrors.email)}
                  aria-describedby={fieldErrors.email ? 'contact-email-error' : undefined}
                />
                {fieldErrors.email && (
                  <span id="contact-email-error" className="landing-field__error" role="status">
                    {fieldErrors.email}
                  </span>
                )}
              </div>
              <div className="landing-field">
                <label htmlFor="contact-message">Message</label>
                <textarea
                  id="contact-message"
                  name="message"
                  rows={4}
                  value={message}
                  onChange={(event) => setMessage(event.target.value)}
                  placeholder="Tell us about your winery…"
                  required
                  aria-invalid={Boolean(fieldErrors.message)}
                  aria-describedby={fieldErrors.message ? 'contact-message-error' : undefined}
                />
                {fieldErrors.message && (
                  <span id="contact-message-error" className="landing-field__error" role="status">
                    {fieldErrors.message}
                  </span>
                )}
              </div>
              <button
                type="submit"
                className="landing-btn landing-btn--primary"
                disabled={isSubmitting}
                aria-busy={isSubmitting}
              >
                {isSubmitting ? 'Sending…' : 'Send message'}
              </button>
            </form>
          </div>
        </section>
      </main>

      <LandingFooter />
    </div>
  );
};

export default Landing;
